import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Settings,
  MessageSquare,
  Wrench,
  Zap,
  Image,
  HelpCircle,
  Star,
  Package,
  FileText,
  Layers,
  Eye,
  Award,
  Building,
  Search,
  LogOut
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Eye, label: 'Hero Slides', path: '/hero-slides' },
    { icon: FileText, label: 'About Us', path: '/about-us' },
    { icon: Award, label: 'Why Choose Us', path: '/why-choose-us' },
    { icon: Layers, label: 'Banners', path: '/banners' },
    { icon: Settings, label: 'Services', path: '/services' },
    { icon: Layers, label: 'Service Categories', path: '/service-categories' },
    { icon: Package, label: 'Products', path: '/products' },
    { icon: FileText, label: 'Blog Posts', path: '/blog-posts' },
    { icon: Layers, label: 'Blog Categories', path: '/blog-categories' },
    { icon: Image, label: 'Gallery', path: '/gallery' },
    { icon: HelpCircle, label: 'FAQs', path: '/faqs' },
    { icon: Star, label: 'Testimonials', path: '/testimonials' },
    { icon: MessageSquare, label: 'Contact Requests', path: '/contact-requests' },
    { icon: Wrench, label: 'Service Requests', path: '/service-requests' },
    ...(user?.role !== 'content_manager' ? [
      { icon: Building, label: 'Company Info', path: '/company-info' },
      { icon: Search, label: 'SEO Settings', path: '/seo-settings' },
      { icon: Users, label: 'Users', path: '/users' }
    ] : [])
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 p-4 flex flex-col
        transition-transform duration-300 ease-in-out transform
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:relative lg:translate-x-0 lg:z-0
      `}>
        <div className="flex items-center justify-between mb-8 px-2">
          <div className="flex items-center gap-2">
            <Zap className="h-8 w-8 text-orange-600" />
            <h1 className="text-xl font-bold text-gray-900 uppercase tracking-tight">BD Admin</h1>
          </div>
          <button onClick={onClose} className="lg:hidden p-2 text-gray-400 hover:text-gray-600">
            <LogOut className="h-5 w-5" /> {/* Using LogOut icon as a close placeholder or similar, but let's just use Menu/X if available? LogOut is already imported. Let's see if we can import X. */}
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto custom-scrollbar">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => {
                if (window.innerWidth < 1024) onClose();
              }}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-xl transition-all font-medium text-sm ${isActive
                  ? 'bg-orange-50 text-orange-600'
                  : 'text-gray-600 hover:bg-gray-50'
                }`
              }
            >
              <item.icon className="h-5 w-5 mr-3" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto pt-4 border-t border-gray-100">
          <button
            onClick={logout}
            className="flex items-center w-full px-4 py-2.5 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all font-medium text-sm"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;